import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useLocation } from 'react-router-dom'
import { blockProperty, unblockProperty } from '../Property_Api'
import { CircularProgress } from '@mui/material'
import { propertyDetailsPropDataType } from '../PropertyDataTypes'
import Swal from 'sweetalert2'

const ViewNewProperty = ({
  propertyDetails,
  id,
  get_view_property,
  propertyImage,
  propertyImageLoading,
}: propertyDetailsPropDataType) => {
  const [propertyStatusLoading, setPropertyStatusLoading] = useState<boolean>(false)
  const location = useLocation()

  // function to extract city, state and country from address
  const extractAddress = () => {
    if (propertyDetails) {
      const addressArray: string[] = propertyDetails?.property_address.split(/,|-/)
      return addressArray.slice(addressArray.length - 4, addressArray.length - 1).join(', ')
    }
  }

  // function to unblock the properties
  const handleUnBlock = async () => {
    setPropertyStatusLoading(true)
    if (id) {
      try {
        const { data: response } = await unblockProperty(id)
        if (response.code === '200') {
          get_view_property()
        } else {
          Swal.fire({
            position: 'center',
            icon: 'error',
            title: response.msg.charAt(0).toUpperCase() + response.msg.slice(1),
            text: response.msg_details,
            allowOutsideClick: true,
          })
        }
      } catch (error: any) {
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: error,
        })
      }
      finally {
        setPropertyStatusLoading(false)
      }
    }
  }

  // function to block properties
  const handleBlock = async () => {
    setPropertyStatusLoading(true)
    if (id) {
      try {
        const { data: response } = await blockProperty(id)
        if (response.code === '200') {
          get_view_property()
        } else {
          Swal.fire({
            position: 'center',
            icon: 'error',
            title: response.msg.charAt(0).toUpperCase() + response.msg.slice(1),
            text: response.msg_details,
            allowOutsideClick: true,
          })
        }
      } catch (error: any) {
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: error,
        })
      }
      finally {
        setPropertyStatusLoading(false)
      }
    }
  }

  return (
    <>
      {propertyDetails && (
        <div className='card mb-5 mb-xl-10'>
          <div className='card-body pt-9 pb-0'>
            <div className='d-flex flex-wrap flex-sm-nowrap mb-3'>
              {/* Begin: property image */}
              <div className='me-7 mb-4'>
                <div className='symbol bg-light-primary symbol-100px symbol-lg-160px symbol-fixed position-relative'>
                  {propertyImageLoading ? (
                    <span
                      className='d-flex justify-content-center align-items-center'
                      style={{
                        minHeight: '100px',
                        minWidth: '100px',
                        height: '160px',
                        width: '160px',
                      }}
                    >
                      <CircularProgress />
                    </span>
                  ) : propertyImage ? (
                    <img src={propertyImage} alt='Property' style={{ objectFit: 'cover' }} />
                  ) : (
                    <iframe
                      src='https://embed.lottiefiles.com/animation/59875'
                      width='200px'
                      title='property icon'
                    ></iframe>
                  )}
                </div>
              </div>
              {/* End: property image */}

              <div className='flex-grow-1'>
                <div className='d-flex justify-content-between align-items-start flex-wrap mb-2'>
                  <div className='d-flex flex-column'>
                    <div className='d-flex align-items-center mb-2'>
                      {/* Begin: Property title */}
                      <span className='text-gray-800 text-hover-primary fs-2 fw-bolder me-1'>
                        {propertyDetails.property_title.charAt(0).toUpperCase() +
                          propertyDetails.property_title.slice(1)}
                      </span>
                      {/* End: Property title */}
                      {/* Begin: Property status block/ unblock icon */}
                      <span>
                        {propertyStatusLoading ? (
                          <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                        ) : propertyDetails.property_status === 1 ? (
                          <i
                            className='bi bi-patch-check-fill fs-2 text-success'
                            title='Property is unblocked'
                          ></i>
                        ) : (
                          <i
                            className='bi bi-patch-exclamation-fill fs-2 text-danger'
                            title='Property is blocked'
                          ></i>
                        )}
                      </span>
                      {/* End: Property status block/ unblock icon */}
                    </div>

                    <div className='d-flex flex-wrap fw-bold fs-6 mb-4 pe-2'>
                      {/* Begin: Property type */}
                      <span className='d-flex align-items-center text-gray-400 text-hover-primary me-5 mb-2'>
                        {propertyDetails.property_usage_type === 1 ? (
                          <i className='bi bi-house-door' />
                        ) : (
                          <i className='bi bi-building'></i>
                        )}
                        {propertyDetails.property_usage_type === 1 ? 'Residential' : 'Commercial'}
                      </span>
                      {/* End: Property type */}
                      {/* Begin: Property furnishing status */}
                      <span className='d-flex align-items-center text-gray-400 text-hover-primary me-5 mb-2'>
                        <i className='bi bi-stars'></i>
                        {propertyDetails.property_furnishing_status === 1
                          ? 'Unfurnished'
                          : propertyDetails.property_furnishing_status === 2
                            ? 'Semi Furnished'
                            : propertyDetails.property_furnishing_status === 3
                              ? 'Fully Furnished'
                              : 'not available'}
                      </span>
                      {/* End: Property furnishing status */}
                      {/* Begin: Property address */}
                      <span className='d-flex align-items-center text-gray-400 text-hover-primary mb-2'>
                        <i className='bi bi-geo-alt'></i>
                        {extractAddress()}
                      </span>
                      {/* End: Property address */}
                    </div>
                  </div>

                  {/* Begin: block / unblock button */}
                  <div className='d-flex my-4'>
                    <button
                      className='btn btn-sm btn-primary me-3'
                      data-bs-toggle='modal'
                      data-bs-target='#kt_modal_offer_a_deal'
                      onClick={propertyDetails.property_status === 1 ? handleBlock : handleUnBlock}
                      disabled={propertyStatusLoading}
                    >
                      {propertyStatusLoading ? (
                        <span className='indicator-progress' style={{ display: 'block' }}>
                          Processing...
                          <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                        </span>
                      ) : propertyDetails.property_status === 1 ? (
                        'Block'
                      ) : (
                        'Unblock'
                      )}
                    </button>
                  </div>
                  {/* End: block / unblock button */}
                </div>

                <div className='d-flex flex-wrap flex-stack'>
                  <div className='d-flex flex-column flex-grow-1 pe-8'>
                    <div className='d-flex flex-wrap'>
                      {/* Begin: Current rent */}
                      <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                        <div className='d-flex align-items-center'>
                          <div className='fs-4 fw-bolder'>{`${propertyDetails.currency_code === 'INR'
                            ? '₹'
                            : propertyDetails.currency_code
                            } ${propertyDetails.current_rent}`}</div>
                        </div>
                        <div className='fw-bold fs-6 text-gray-400'>Rent</div>
                      </div>
                      {/* End: Current rent */}
                      {/* Begin: Carpet area */}
                      <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                        <div className='d-flex align-items-center'>
                          <div className='fs-4 fw-bolder'>{`${propertyDetails.carpet_area} ${propertyDetails.carpet_area_unit_name.toLowerCase() ===
                            'Square Feet'.toLowerCase()
                            ? 'sq. ft.'
                            : 'sq. m.'
                            }`}</div>
                        </div>
                        <div className='fw-bold fs-6 text-gray-400'>Carpet area</div>
                      </div>
                      {/* Begin: Carpet area */}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Begin: Tabs */}
            <div className='d-flex overflow-auto h-55px'>
              <ul className='nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-5 fw-bolder flex-nowrap'>
                {/* Begin: overview tab */}
                <li className='nav-item'>
                  <Link
                    className={
                      `nav-link text-active-primary me-6 ` +
                      (location.pathname === `/property/${id}/overview` && 'active')
                    }
                    to='overview'
                  >
                    Overview
                  </Link>
                </li>
                {/* End: overview tab */}
                {/* Begin: Subdivisions tab */}
                <li className='nav-item'>
                  <Link
                    className={
                      `nav-link text-active-primary me-6 ` +
                      (location.pathname === `/property/${id}/subdivisions` && 'active')
                    }
                    to='subdivisions'
                  >
                    Subdivisions
                  </Link>
                </li>
                {/* End: Subdivisions tab */}
                {/* Begin: Images tab */}
                <li className='nav-item'>
                  <Link
                    className={
                      `nav-link text-active-primary me-6 ` +
                      (location.pathname === `/property/${id}/images` && 'active')
                    }
                    to='images'
                  >
                    Images
                  </Link>
                </li>
                {/* End: Images tab */}
                {/* Begin: Documents tab */}
                <li className='nav-item'>
                  <Link
                    className={
                      `nav-link text-active-primary me-6 ` +
                      (location.pathname === `/property/${id}/documents` && 'active')
                    }
                    to='documents'
                  >
                    Documents
                  </Link>
                </li>
                {/* End: Documents tab */}
                {/* Begin: Inventory tab */}
                <li className='nav-item'>
                  <Link
                    className={
                      `nav-link text-active-primary me-6 ` +
                      (location.pathname === `/property/${id}/inventory` && 'active')
                    }
                    to='inventory'
                  >
                    Inventory
                  </Link>
                </li>
                {/* End: Inventory tab */}
                {/* Begin: Create agreement tab */}
                <li className='nav-item'>
                  <Link
                    className={
                      `nav-link text-active-primary me-6 ` +
                      (location.pathname === `/property/${id}/agreement` && 'active')
                    }
                    to='agreement'
                  >
                    Agreement
                  </Link>
                </li>
                {/* End: Create agreement tab */}
                {/* Begin: gst tab */}
                <li className='nav-item'>
                  <Link
                    className={
                      `nav-link text-active-primary me-6 ` +
                      (location.pathname === `/property/${id}/gst` && 'active')
                    }
                    to='gst'
                  >
                    GST
                  </Link>
                </li>
                {/* End: gst tab */}
              </ul>
            </div>
            {/* End: Tabs */}
          </div>
        </div>
      )}
    </>
  )
}

export default ViewNewProperty
